import { createJsonQuery, declareParams } from '@farfetched/core'
import { getSuggestionById, getSuggestionsSearchUrl } from 'src/features/suggestions/api'
import { ISuggestion, Suggestion } from 'src/features/suggestions/contract'
import { runtypeContract } from '@farfetched/runtypes'
import { Array } from 'runtypes'
import { combine, createEffect, createEvent, createStore, sample } from 'effector'
import { debounce, delay } from 'patronum'
import { IShipment } from 'src/shared/contract/shipment'
import { ICatalogGoodBase } from '@entities/goods/contract'
import { $goods } from '@entities/goods/model'
import { $shipment } from '@entities/shipment/model'
import { subscribeLoggerEffect, writeError } from 'src/infastruct/effector-utils/subscribe-sentry'
import { $currentPath, onRouteChangeComplete, onRouteChangeStart } from '@entities/route/model'
import { pushFx } from 'src/infastruct/effector-utils/attach-router'
import { $currentSuggestionId } from 'src/pages/suggestions/model'
import { $historySuggestion, pushHistorySuggestion } from 'src/features/suggestions/history'

export const suggestionsSearchEvent = createEvent<{ term: string }>()

export const cancelSuggestionsSearch = createEvent()

export const $selectedSuggestion = createStore<ISuggestion>({} as ISuggestion)
const reselectSuggestion = createEvent()
export const selectSuggestion = createEvent<string>()
export const searchSuggestion = createEvent<string>()
export const initSuggestion = createEvent()

sample({
    clock: selectSuggestion,
    fn: () => {
        const elm = document.activeElement as HTMLElement
        elm?.blur()
    }
})
sample({
    clock: searchSuggestion,
    fn: () => {
        const elm = document.activeElement as HTMLElement
        elm?.blur()
    }
})

sample({
    clock: searchSuggestion,
    filter: (term) => term.length > 2,
    fn: (term): ISuggestion => ({
        id: term,
        title: term,
        sortOrder: 999,
        suggestionTypeId: -1,
        suggestionTypeTitle: 'Все результаты поиска'
    }),
    target: $selectedSuggestion
})
sample({
    clock: reselectSuggestion,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    target: $selectedSuggestion.reinit!
})

sample({ clock: $selectedSuggestion, target: initSuggestion })

const suggestionsSearch = debounce({ source: suggestionsSearchEvent, timeout: 300 })

const suggestionsSearchAbort = createEvent()
export const suggestionsSearchModel = createJsonQuery({
    params: declareParams<{ term: string }>(),
    name: 'suggestionsSearch',
    request: {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
        },
        url: ({ term }) => getSuggestionsSearchUrl(term)
    },
    initialData: [] as ISuggestion[],
    response: {
        contract: runtypeContract(Array(Suggestion))
    },
    concurrency: {
        abort: suggestionsSearchAbort,
        strategy: 'TAKE_LATEST'
    },
    serialize: 'ignore'
})
export const $suggestSearchPending = createStore(false)
const stopPending = createEvent()
const restorePending = createEvent<boolean>()

debounce({
    source: suggestionsSearchModel.$pending,
    timeout: 200,
    target: $suggestSearchPending
})

/*sample({
    clock: $suggestSearchPending,
    filter: $suggestSearchPending,
    target: restorePending
})

delay({
    source: restorePending,
    target: stopPending,
    timeout: 5000
})

sample({
    clock: stopPending,
    filter: $suggestSearchPending,
    target: $suggestSearchPending.reinit!
})*/

sample({
    clock: suggestionsSearchModel.$error,
    filter: (clock) => !!clock,
    target: writeError
})

sample({
    clock: selectSuggestion,
    source: { suggestions: suggestionsSearchModel.$data, history: $historySuggestion },
    fn: ({ suggestions, history }, suggestionId): ISuggestion => {
        const id = parseInt(suggestionId) || suggestionId
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return suggestions.find((x) => x.id === id) || history.find((x) => x.id === id)!
    },
    target: $selectedSuggestion
})

export const startSuggestionFx = createEffect<
    { shipment: IShipment; suggestionId: string | number },
    ICatalogGoodBase[],
    Error
>(async ({ suggestionId, shipment }) => {
    return await getSuggestionById({ suggestionId, shipment })
})

subscribeLoggerEffect(startSuggestionFx)

export const $isPendingSuggestion = startSuggestionFx.pending

sample({
    clock: startSuggestionFx.doneData,
    target: $goods
})

sample({
    clock: $shipment,
    source: { suggestionId: $currentSuggestionId, currentPath: $currentPath },
    filter: ({ currentPath }) => currentPath.includes('/catalog/suggestions'),
    fn: ({ suggestionId }, shipment) => ({
        shipment,
        suggestionId
    }),
    target: startSuggestionFx
})

sample({
    clock: $selectedSuggestion,
    target: pushHistorySuggestion
})

sample({
    clock: initSuggestion,
    source: { path: $currentPath, suggestion: $selectedSuggestion },
    filter: ({ path, suggestion }) => {
        return (
            suggestion.suggestionTypeId !== -1 &&
            !path.includes(`/catalog/suggestions/${suggestion.id}`) &&
            !!suggestion.id
        )
    },
    fn: ({ suggestion }) => `/catalog/suggestions/${suggestion.id}`,
    target: [pushFx, reselectSuggestion]
})

sample({
    clock: initSuggestion,
    source: { path: $currentPath, shipment: $shipment, suggestion: $selectedSuggestion },
    filter: ({ path, suggestion }) =>
        suggestion.suggestionTypeId !== -1 && path.includes(`/catalog/suggestions/${suggestion.id}`) && !!suggestion.id,
    fn: ({ shipment, suggestion }) => ({
        shipment,
        suggestionId: suggestion.id
    }),
    target: [startSuggestionFx, reselectSuggestion]
})
sample({
    clock: initSuggestion,
    source: { path: $currentPath, suggestion: $selectedSuggestion },
    filter: ({ suggestion }) => suggestion.suggestionTypeId === -1,
    fn: ({ suggestion }) => ({
        pathname: '/catalog/search',
        query: { q: suggestion.title }
    }),
    target: [pushFx, reselectSuggestion]
})

sample({
    clock: $currentPath,
    source: {
        shipment: $shipment,
        suggestionId: $currentSuggestionId
    },
    filter: ({ suggestionId }, path) => path.includes('/catalog/suggestions') && !!suggestionId,
    fn: ({ shipment, suggestionId }) => ({ shipment, suggestionId }),
    target: startSuggestionFx
})

export const $foundSuggestions = suggestionsSearchModel.$data
export const $isFoundSuggestions = combine(suggestionsSearchModel.$data, (state) => !!state?.length)

sample({
    clock: suggestionsSearch,
    target: suggestionsSearchModel.start
})

sample({
    clock: cancelSuggestionsSearch,
    target: suggestionsSearchModel.reset
})

sample({
    clock: onRouteChangeStart,
    target: suggestionsSearchAbort
})
sample({
    clock: onRouteChangeComplete,
    target: suggestionsSearchAbort
})
sample({
    clock: onRouteChangeComplete,
    target: suggestionsSearchModel.reset
})
sample({
    clock: onRouteChangeStart,
    target: suggestionsSearchModel.reset
})
